import React, { useState, useRef, useEffect } from 'react';
import { 
  Paperclip, 
  Mic, 
  Send, 
  BarChart3, 
  TrendingUp, 
  Calculator,
  MessageSquare,
  Bot,
  Menu,
  X,
  Plus,
  Zap,
  FolderOpen,
  History,
  Trash2,
  User,
  Loader,
  Search,
  Home
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import LoadingOverlay from './LoadingOverlay';
import HierarchicalFileSelector from './HierarchicalFileSelector';
import './ChatHome.css';
import config, { getDefaultHeaders } from '../config/api';
import { useCountry } from '../context/CountryContext';

const ChatHome = ({ onMenuClick, onNavigateHome, conversationId, loadedMessages, onClearConversation }) => {
  const { country } = useCountry();
  const [inputValue, setInputValue] = useState('');
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [chatMode, setChatMode] = useState('auto');

  // Page loading effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsPageLoading(false);
    }, 1500);

    return () => clearTimeout(timer);
  }, []);

  // Load conversation when props change
  useEffect(() => {
    if (loadedMessages && loadedMessages.length > 0) {
      // Convert timestamp strings to Date objects
      const messagesWithDateObjects = loadedMessages.map(message => ({
        ...message,
        timestamp: new Date(message.timestamp)
      }));
      setMessages(messagesWithDateObjects);
    } else if (conversationId === null) {
      // Clear messages when no conversation is loaded
      setMessages([]);
    }
  }, [conversationId, loadedMessages]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [showHierarchicalFileSelector, setShowHierarchicalFileSelector] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [availableFilesCount, setAvailableFilesCount] = useState(99);
  
  // Conversation Memory State
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [showConversations, setShowConversations] = useState(false);
  const [loadingConversation, setLoadingConversation] = useState(false);
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const API_BASE_URL = config.apiBaseUrl;

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Handle scroll detection for scroll-to-top button
  useEffect(() => {
    const messagesContainer = messagesContainerRef.current;
    if (!messagesContainer) return;

    const handleScroll = () => {
      const scrollTop = messagesContainer.scrollTop;
      const scrollHeight = messagesContainer.scrollHeight;
      const clientHeight = messagesContainer.clientHeight;
      
      // Show button when there's scrollable content and user has scrolled down
      setShowScrollToTop(scrollHeight > clientHeight && scrollTop > 100);
    };

    // Initial check
    handleScroll();
    
    messagesContainer.addEventListener('scroll', handleScroll);
    return () => messagesContainer.removeEventListener('scroll', handleScroll);
  }, [messages]); // Re-run when messages change

  const scrollToTop = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };

  // Load conversations on component mount
  useEffect(() => {
    loadConversations();
  }, []);

  const loadConversations = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/chat/conversations`, {
        headers: getDefaultHeaders(country)
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setConversations(data.conversations || []);
        }
      }
    } catch (error) {
      console.error('Error loading conversations:', error);
    }
  };

  const startNewConversation = () => {
    setCurrentConversationId(null);
    setMessages([]);
    setSelectedFiles([]);
    setShowConversations(false);
    if (onClearConversation) {
      onClearConversation();
    }
  };

  const loadConversation = async (conversationId) => {
    try {
      setLoadingConversation(true);
      const response = await fetch(`${API_BASE_URL}/api/chat/conversations/${conversationId}`, {
        headers: getDefaultHeaders(country)
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // Convert backend message format to frontend format
          const frontendMessages = data.messages.map(msg => ({
            id: msg.id,
            type: msg.type,
            content: msg.content,
            timestamp: new Date(msg.timestamp),
            files_used: msg.files_used,
            token_count: msg.token_count,
            processing_time: msg.processing_time,
            complexity: msg.complexity,
            expected_length: msg.expected_length
          }));
          
          setCurrentConversationId(conversationId);
          setMessages(frontendMessages);
          
          // Restore mode and selected files from conversation
          if (data.mode) {
            setChatMode(data.mode);
          }
          if (data.selected_files && data.selected_files.length > 0) {
            setSelectedFiles(data.selected_files);
          } else {
            setSelectedFiles([]);
          }
          
          setShowConversations(false);
        } else {
          console.error('Failed to load conversation:', data.error);
          setMessages([{
            id: Date.now(),
            type: 'ai',
            content: `Failed to load conversation: ${data.error}`,
            timestamp: new Date(),
            isError: true
          }]);
        }
      } else {
        console.error('Failed to load conversation:', response.statusText);
        setMessages([{
          id: Date.now(),
          type: 'ai',
          content: `Failed to load conversation: ${response.statusText}`,
          timestamp: new Date(),
          isError: true
        }]);
      }
    } catch (error) {
      console.error('Error loading conversation:', error);
      setMessages([{
        id: Date.now(),
        type: 'ai',
        content: `Error loading conversation: ${error.message}`,
        timestamp: new Date(),
        isError: true
      }]);
    } finally {
      setLoadingConversation(false);
    }
  };

  const deleteConversation = async (conversationId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/chat/conversations/${conversationId}`, {
        method: 'DELETE',
        headers: getDefaultHeaders(country),
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // Remove from local state
          setConversations(prev => prev.filter(conv => conv.conversation_id !== conversationId));
          
          // If this was the current conversation, start a new one
          if (currentConversationId === conversationId) {
            startNewConversation();
          }
        }
      }
    } catch (error) {
      console.error('Error deleting conversation:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!inputValue.trim() || isLoading) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: inputValue,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      let response;
      
      if (chatMode === 'manual' && selectedFiles.length === 0) {
        // Show file selector if manual mode but no files selected
        setShowHierarchicalFileSelector(true);
        setIsLoading(false);
        return;
      }

      const requestBody = {
        query: inputValue,
        conversation_id: currentConversationId
      };

      if (chatMode === 'manual') {
        // Manual mode: use selected files
        requestBody.selected_files = selectedFiles;
        response = await fetch(`${API_BASE_URL}/api/chat/ask-manual`, {
          method: 'POST',
          headers: getDefaultHeaders(country),
          body: JSON.stringify(requestBody),
        });
      } else {
        // Auto mode: let AI select files
        response = await fetch(`${API_BASE_URL}/api/chat/ask`, {
          method: 'POST',
          headers: getDefaultHeaders(country),
          body: JSON.stringify(requestBody),
        });
      }

      if (response.ok) {
        const data = await response.json();
        
        if (data.success) {
          // Update conversation ID if this is a new conversation
          if (data.conversation_id && !currentConversationId) {
            setCurrentConversationId(data.conversation_id);
          }
          
          const aiMessage = {
            id: Date.now() + 1,
            type: 'ai',
            content: data.response,
            timestamp: new Date(),
            files_used: data.files_used || [],
            token_count: data.token_count || 0,
            processing_time: data.processing_time || 0,
            complexity: data.complexity || 'moderate',
            expected_length: data.expected_length || 'detailed'
          };

          setMessages(prev => [...prev, aiMessage]);
          
          // Reload conversations to get updated list
          loadConversations();
          
          // Refresh sidebar conversations
          if (window.refreshSidebarConversations) {
            window.refreshSidebarConversations();
          }
        } else {
          const errorMessage = {
            id: Date.now() + 1,
            type: 'ai',
            content: data.response || 'Sorry, I encountered an error while processing your request.',
            timestamp: new Date(),
            isError: true
          };
          setMessages(prev => [...prev, errorMessage]);
        }
      } else {
        const errorMessage = {
          id: Date.now() + 1,
          type: 'ai',
          content: 'Sorry, I encountered an error while processing your request.',
          timestamp: new Date(),
          isError: true
        };
        setMessages(prev => [...prev, errorMessage]);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = {
        id: Date.now() + 1,
        type: 'ai',
        content: 'Sorry, I encountered an error while processing your request.',
        timestamp: new Date(),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const renderMessage = (message) => {
    const isUser = message.type === 'user';
    
    return (
      <div key={message.id} className={`chat-message ${isUser ? 'user' : 'ai'} ${message.isError ? 'error' : ''}`}>
        <div className="message-avatar">
          {isUser ? <User size={16} /> : <Bot size={16} />}
        </div>
        <div className="message-content">
          <div className="message-timestamp">
            {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}, {message.timestamp.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
          </div>
          <div className="message-text">
            <ReactMarkdown 
              remarkPlugins={[remarkGfm]}
              components={{
                h1: (props) => <h1 className="markdown-h1" {...props} />,
                h2: (props) => <h2 className="markdown-h2" {...props} />,
                h3: (props) => <h3 className="markdown-h3" {...props} />,
                h4: (props) => <h4 className="markdown-h4" {...props} />,
                h5: (props) => <h5 className="markdown-h5" {...props} />,
                h6: (props) => <h6 className="markdown-h6" {...props} />,
                p: (props) => <p className="markdown-p" {...props} />,
                strong: (props) => <strong className="markdown-strong" {...props} />,
                em: (props) => <em className="markdown-em" {...props} />,
                ul: (props) => <ul className="markdown-ul" {...props} />,
                ol: (props) => <ol className="markdown-ol" {...props} />,
                li: (props) => <li className="markdown-li" {...props} />,
                table: (props) => <table className="markdown-table" {...props} />,
                thead: (props) => <thead className="markdown-thead" {...props} />,
                tbody: (props) => <tbody className="markdown-tbody" {...props} />,
                tr: (props) => <tr className="markdown-tr" {...props} />,
                th: (props) => <th className="markdown-th" {...props} />,
                td: (props) => <td className="markdown-td" {...props} />,
                code: (props) => <code className="markdown-code" {...props} />,
                pre: (props) => <pre className="markdown-pre" {...props} />,
                blockquote: (props) => <blockquote className="markdown-blockquote" {...props} />,
                hr: (props) => <hr className="markdown-hr" {...props} />,
              }}
            >
              {message.content}
            </ReactMarkdown>
          </div>
          
          {!isUser && !message.isError && (
            <div className="message-meta">
              <div className="message-stats">
                <span>Tokens: {message.token_count?.toLocaleString() || 'N/A'}</span>
                <span>Time: {message.processing_time?.toFixed(2)}s</span>
                {message.expected_length && (
                  <span>Length: {message.expected_length}</span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const popularQuestions = [
    {
      id: 1,
      icon: TrendingUp,
      question: "Why did net interest margin improve this quarter?",
      type: "Analysis",
      typeColor: "blue"
    },
    {
      id: 2,
      icon: TrendingUp,
      question: "What drove the increase in loan loss provisions?",
      type: "Analysis",
      typeColor: "blue"
    },
    {
      id: 3,
      icon: Calculator,
      question: "What if loan growth was 15% next quarter?",
      type: "Scenario",
      typeColor: "green"
    }
  ];

  const handleQuestionClick = (question) => {
    setInputValue(question);
  };

  const handleFileSelection = (files) => {
    setSelectedFiles(files);
    // Don't close modal automatically - let user close manually
  };

  const clearSelectedFiles = () => {
    setSelectedFiles([]);
  };

  const handleHierarchicalFileSelection = (files) => {
    setSelectedFiles(files);
    // Don't close the modal here - let user select multiple files
    // Modal will close when user clicks "Select Files" button
  };

  // State for expanded folders
  const [expandedFolders, setExpandedFolders] = useState(new Set());

  // Load file mapping data
  const [fileMapping, setFileMapping] = useState(null);
  const [loadingFiles, setLoadingFiles] = useState(true);
  
  // Hierarchical file selection state
  const [hierarchicalData, setHierarchicalData] = useState({});
  const [fileSelectionStats, setFileSelectionStats] = useState({
    totalFiles: 0,
    selectedFiles: 0,
    selectedBanks: new Set(),
    selectedQuarters: new Set(),
    selectedFolders: new Set()
  });

  useEffect(() => {
    const loadFileMapping = async () => {
      try {
        // Try multiple possible paths for the output_mapping.json file
        const possiblePaths = [
          '/output_mapping.json',
          './output_mapping.json',
          '../output_mapping.json',
          '/api/output_mapping.json'
        ];
        
        let data = null;
        let lastError = null;
        
        for (const path of possiblePaths) {
          try {
            console.log(`Trying to load file mapping from: ${path}`);
            const response = await fetch(path);
            
            if (!response.ok) {
              throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            
            data = await response.json();
            console.log('Successfully loaded file mapping:', data);
            break;
          } catch (error) {
            console.log(`Failed to load from ${path}:`, error.message);
            lastError = error;
          }
        }
        
        if (data) {
          console.log('Loaded file mapping with', data.metadata.total_summary_files, 'files');
          setFileMapping(data);
          setAvailableFilesCount(data.metadata.total_summary_files);
        } else {
          throw lastError || new Error('Could not load file mapping from any path');
        }
      } catch (error) {
        console.error('Error loading file mapping:', error);
        setAvailableFilesCount(0);
        // Set a fallback file mapping with sample data for testing
        setFileMapping({
          metadata: { total_summary_files: 12 },
          summary_files: {
            "ADIB": {
              "2024": {
                "Q1": {
                  "presentation": {
                    "key_highlights": {
                      "summary_adib_q1_24_key_highlights.md": {
                        type: "file",
                        relative_path: "ADIB\\2024\\Q1\\presentation\\key_highlights\\summary_adib_q1_24_key_highlights.md",
                        description: "Adib - 2024 - Q1 - Presentation - Key Highlights",
                        file_size: 7846,
                        bank: "ADIB",
                        year: "2024",
                        quarter: "Q1",
                        document_type: "presentation"
                      }
                    },
                    "retail_banking": {
                      "summary_adib_q1_24_retail_banking.md": {
                        type: "file",
                        relative_path: "ADIB\\2024\\Q1\\presentation\\retail_banking\\summary_adib_q1_24_retail_banking.md",
                        description: "Adib - 2024 - Q1 - Presentation - Retail Banking",
                        file_size: 15004,
                        bank: "ADIB",
                        year: "2024",
                        quarter: "Q1",
                        document_type: "presentation"
                      }
                    }
                  },
                  "transcript": {
                    "summary_abid_q1_24_transcript.md": {
                      type: "file",
                      relative_path: "ADIB\\2024\\Q1\\transcript\\summary_abid_q1_24_transcript.md",
                      description: "Adib - 2024 - Q1 - Transcript",
                      file_size: 11055,
                      bank: "ADIB",
                      year: "2024",
                      quarter: "Q1",
                      document_type: "transcript"
                    }
                  }
                },
                "Q2": {
                  "presentation": {
                    "key_highlights": {
                      "summary_adib_q2_24_key_highlights.md": {
                        type: "file",
                        relative_path: "ADIB\\2024\\Q2\\presentation\\key_highlights\\summary_adib_q2_24_key_highlights.md",
                        description: "Adib - 2024 - Q2 - Presentation - Key Highlights",
                        file_size: 8863,
                        bank: "ADIB",
                        year: "2024",
                        quarter: "Q2",
                        document_type: "presentation"
                      }
                    }
                  },
                  "transcript": {
                    "summary_abid_q2_24_transcript.md": {
                      type: "file",
                      relative_path: "ADIB\\2024\\Q2\\transcript\\summary_abid_q2_24_transcript.md",
                      description: "Adib - 2024 - Q2 - Transcript",
                      file_size: 10517,
                      bank: "ADIB",
                      year: "2024",
                      quarter: "Q2",
                      document_type: "transcript"
                    }
                  }
                }
              }
            },
            "DIB": {
              "2024": {
                "Q1": {
                  "presentation": {
                    "key_highlights": {
                      "summary_dib_q1_24_key_highlights.md": {
                        type: "file",
                        relative_path: "DIB\\2024\\Q1\\presentation\\key_highlights\\summary_dib_q1_24_key_highlights.md",
                        description: "Dib - 2024 - Q1 - Presentation - Key Highlights",
                        file_size: 7898,
                        bank: "DIB",
                        year: "2024",
                        quarter: "Q1",
                        document_type: "presentation"
                      }
                    }
                  },
                  "transcript": {
                    "summary_dib_q1_24_transcript.md": {
                      type: "file",
                      relative_path: "DIB\\2024\\Q1\\transcript\\summary_dib_q1_24_transcript.md",
                      description: "Dib - 2024 - Q1 - Transcript",
                      file_size: 8500,
                      bank: "DIB",
                      year: "2024",
                      quarter: "Q1",
                      document_type: "transcript"
                    }
                  }
                }
              }
            },
            "ENBD": {
              "2024": {
                "Q1": {
                  "presentation": {
                    "key_highlights": {
                      "summary_enbd_q1_24_key_highlights.md": {
                        type: "file",
                        relative_path: "ENBD\\2024\\Q1\\presentation\\key_highlights\\summary_enbd_q1_24_key_highlights.md",
                        description: "Enbd - 2024 - Q1 - Presentation - Key Highlights",
                        file_size: 9105,
                        bank: "ENBD",
                        year: "2024",
                        quarter: "Q1",
                        document_type: "presentation"
                      }
                    }
                  },
                  "transcript": {
                    "summary_enbd_q1_24_transcript.md": {
                      type: "file",
                      relative_path: "ENBD\\2024\\Q1\\transcript\\summary_enbd_q1_24_transcript.md",
                      description: "Enbd - 2024 - Q1 - Transcript",
                      file_size: 12965,
                      bank: "ENBD",
                      year: "2024",
                      quarter: "Q1",
                      document_type: "transcript"
                    }
                  }
                }
              }
            },
            "FAB": {
              "2024": {
                "Q1": {
                  "presentation": {
                    "key_highlights": {
                      "summary_fab_q1_24_key_highlights.md": {
                        type: "file",
                        relative_path: "FAB\\2024\\Q1\\presentation\\key_highlights\\summary_fab_q1_24_key_highlights.md",
                        description: "Fab - 2024 - Q1 - Presentation - Key Highlights",
                        file_size: 7062,
                        bank: "FAB",
                        year: "2024",
                        quarter: "Q1",
                        document_type: "presentation"
                      }
                    }
                  },
                  "transcript": {
                    "summary_fab_q1_24_transcript.md": {
                      type: "file",
                      relative_path: "FAB\\2024\\Q1\\transcript\\summary_fab_q1_24_transcript.md",
                      description: "Fab - 2024 - Q1 - Transcript",
                      file_size: 11341,
                      bank: "FAB",
                      year: "2024",
                      quarter: "Q1",
                      document_type: "transcript"
                    }
                  }
                }
              }
            }
          }
        });
        setAvailableFilesCount(12);
      } finally {
        setLoadingFiles(false);
      }
    };

    loadFileMapping();
  }, []);

  const toggleFolder = (folderPath) => {
    setExpandedFolders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(folderPath)) {
        newSet.delete(folderPath);
      } else {
        newSet.add(folderPath);
      }
      return newSet;
    });
  };

  const toggleFileSelection = (fileId) => {
    setSelectedFiles(prev => {
      if (prev.includes(fileId)) {
        return prev.filter(id => id !== fileId);
      } else {
        return [...prev, fileId];
      }
    });
  };












  return (
    <div className="chat-home">
      {/* Loading Overlay */}
      <LoadingOverlay
        title="AI Earnings Assistant"
        message="Initializing your chat assistant..."
        show={isPageLoading}
      />


      {/* Conversations Sidebar */}
      {showConversations && (
        <div className="conversations-sidebar">
          <div className="conversations-header">
            <h4>Conversations</h4>
            <button
              className="close-btn"
              onClick={() => setShowConversations(false)}
            >
              <X size={16} />
            </button>
          </div>
          <div className="conversations-list">
            {conversations.length === 0 ? (
              <div className="no-conversations">
                <p>No conversations yet</p>
                <button
                  className="new-conversation-btn"
                  onClick={startNewConversation}
                >
                  Start New Conversation
                </button>
              </div>
            ) : (
              conversations.map((conversation) => (
                <div
                  key={conversation.conversation_id}
                  className={`conversation-item ${currentConversationId === conversation.conversation_id ? 'active' : ''} ${loadingConversation && currentConversationId === conversation.conversation_id ? 'loading' : ''}`}
                  onClick={() => loadConversation(conversation.conversation_id)}
                >
                  <div className="conversation-content">
                    <div className="conversation-query">
                      {conversation.last_query || 'No query'}
                    </div>
                    <div className="conversation-meta">
                      <span className="conversation-time">
                        {new Date(conversation.timestamp).toLocaleDateString()}
                      </span>
                      <span className="conversation-files">
                        {conversation.file_count} files
                      </span>
                      <span className="conversation-messages">
                        {conversation.message_count} messages
                      </span>
                      <span className={`conversation-mode ${conversation.mode || 'auto'}`}>
                        {conversation.mode || 'auto'}
                      </span>
                    </div>
                  </div>
                  <button
                    className="conversation-delete-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteConversation(conversation.conversation_id);
                    }}
                    title="Delete this conversation"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="chat-main">
        {messages.length === 0 ? (
          <div className="welcome-section">
            <div className="welcome-content">
              {/* New Title and Subtitle */}
              <div className="welcome-header">
                <h1 className="welcome-title">
                  <span className="highlight">What can I help with?</span>
                </h1>
                <p className="welcome-subtitle">
                  Your AI-powered earnings assistant for banking insights, variance analysis, and strategic planning.
                </p>
              </div>

              <div className="mode-toggle-container">
                <div className="mode-toggle">
                  <button
                    className={`mode-btn ${chatMode === 'auto' ? 'active' : ''}`}
                    onClick={() => setChatMode('auto')}
                  >
                    <Zap size={14} />
                    Auto
                  </button>
                  <button
                    className={`mode-btn ${chatMode === 'manual' ? 'active' : ''}`}
                    onClick={() => setChatMode('manual')}
                  >
                    <FolderOpen size={14} />
                    Manual
                  </button>
                </div>
                
                {/* Chat History and New Conversation Buttons */}
                <div className="chat-controls">
                  <button
                    className="chat-control-btn"
                    onClick={() => setShowConversations(!showConversations)}
                    title="Conversations"
                  >
                    <History size={16} />
                    History
                  </button>
                  <button
                    className="chat-control-btn"
                    onClick={startNewConversation}
                    title="New Conversation"
                  >
                    <Plus size={16} />
                    New Chat
                  </button>
                </div>
              </div>
              
                             {/* File Selection Bar (Manual Mode) */}
               {chatMode === 'manual' && (
                 <div className="file-selection-bar">
                   <button 
                     className="file-select-btn"
                     onClick={() => setShowHierarchicalFileSelector(true)}
                   >
                     <FolderOpen size={14} />
                     {selectedFiles.length > 0 ? `${selectedFiles.length} files` : 'Select Files'}
                   </button>
                   
                   {selectedFiles.length > 0 && (
                     <div className="selected-files-compact">
                       <div className="selected-files-list">
                         {selectedFiles.map((fileId, index) => {
                           // Extract a more readable filename from the path
                           const fileName = fileId.split('/').pop() || fileId;
                           const displayName = fileName.replace('.md', '').replace(/summary_/g, '').replace(/_/g, ' ');
                           return (
                             <span key={index} className="selected-file-name" title={fileId}>
                               {displayName}
                             </span>
                           );
                         })}
                       </div>
                     </div>
                   )}
                 </div>
               )}

               {/* Input Field */}
               <form className="main-input-form" onSubmit={handleSubmit}>
                 <div className="input-container">
                   <button type="button" className="input-icon">
                     <Paperclip size={20} />
                   </button>
                   <input
                     type="text"
                     value={inputValue}
                     onChange={(e) => setInputValue(e.target.value)}
                     placeholder={
                       chatMode === 'manual' && selectedFiles.length === 0
                         ? "Select files first, then ask your question..."
                         : "Ask about earnings, run scenarios, or request analysis..."
                     }
                     className="main-input"
                     disabled={isLoading}
                   />
                   <button type="button" className="input-icon">
                     <Mic size={20} />
                   </button>
                   <button type="submit" className="send-btn" disabled={isLoading || !inputValue.trim() || (chatMode === 'manual' && selectedFiles.length === 0)}>
                     <Send size={20} />
                   </button>
                 </div>
                 <div className="input-help">
                   <span>Press Enter to send, Shift+Enter for new line</span>
                   <span className="char-count">{inputValue.length}/2000</span>
                 </div>
               </form>

              {/* Popular Questions */}
              <div className="popular-questions">
                <h3>Popular questions</h3>
                <p>Get started with these common financial analysis queries</p>
                <div className="questions-grid">
                  {popularQuestions.map((item) => (
                    <div key={item.id} className="question-card" onClick={() => handleQuestionClick(item.question)}>
                      <div className="question-icon">
                        <item.icon size={20} />
                      </div>
                      <div className="question-content">
                        <p>{item.question}</p>
                        <span className={`question-type ${item.typeColor}`}>{item.type}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
                 ) : (
           <div className="chat-conversation">
             <div className="chat-conversation-header">
               <button 
                 className="back-to-home-btn"
                 onClick={() => setMessages([])}
                 title="Back to Home"
               >
                 <Home size={16} />
                 Back to Home
               </button>
               
               <div className="chat-conversation-controls">
                 <div className="mode-toggle">
                   <button
                     className={`mode-btn ${chatMode === 'auto' ? 'active' : ''}`}
                     onClick={() => setChatMode('auto')}
                   >
                     <Zap size={14} />
                     Auto
                   </button>
                   <button
                     className={`mode-btn ${chatMode === 'manual' ? 'active' : ''}`}
                     onClick={() => setChatMode('manual')}
                   >
                     <FolderOpen size={14} />
                     Manual
                   </button>
                 </div>
                 
                 <div className="chat-controls">
                   <button
                     className="chat-control-btn"
                     onClick={() => setShowConversations(!showConversations)}
                     title="Conversations"
                   >
                     <History size={16} />
                     History
                   </button>
                   <button
                     className="chat-control-btn"
                     onClick={startNewConversation}
                     title="New Conversation"
                   >
                     <Plus size={16} />
                     New Chat
                   </button>
                 </div>
               </div>
             </div>
             <div className="messages-container" ref={messagesContainerRef}>
              {messages.map(renderMessage)}
              
              {isLoading && (
                <div className="chat-message ai">
                  <div className="message-avatar">
                    <Bot size={16} />
                  </div>
                  <div className="message-content">
                    <div className="message-timestamp">
                      {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}, {new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                    </div>
                    <div className="message-text">
                      <div className="loading-indicator">
                        <Loader size={16} className="spinning" />
                        <span>Analysing your Request...</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>
            
            {/* Scroll to Top Button */}
            {showScrollToTop && (
              <button
                className="scroll-to-top-btn"
                onClick={scrollToTop}
                title="Scroll to top"
              >
                ↑
              </button>
            )}
            

                         {/* File Selection Bar (Manual Mode) */}
             {chatMode === 'manual' && (
               <div className="file-selection-bar">
                 <button 
                   className="file-select-btn"
                   onClick={() => setShowHierarchicalFileSelector(true)}
                 >
                   <FolderOpen size={14} />
                   {selectedFiles.length > 0 ? `${selectedFiles.length} files` : 'Select Files'}
                 </button>
                 
                 {selectedFiles.length > 0 && (
                   <div className="selected-files-compact">
                     <div className="selected-files-list">
                       {selectedFiles.map((fileId, index) => {
                         // Extract a more readable filename from the path
                         const fileName = fileId.split('/').pop() || fileId;
                         const displayName = fileName.replace('.md', '').replace(/summary_/g, '').replace(/_/g, ' ');
                         return (
                           <span key={index} className="selected-file-name" title={fileId}>
                             {displayName}
                           </span>
                         );
                       })}
                     </div>
                   </div>
                 )}
               </div>
             )}

             {/* Chat Input */}
             <div className="chat-input-container">
               <form className="chat-input-form" onSubmit={handleSubmit}>
                 <div className="input-container">
                   <button type="button" className="input-icon">
                     <Paperclip size={20} />
                   </button>
                   <input
                     type="text"
                     value={inputValue}
                     onChange={(e) => setInputValue(e.target.value)}
                     placeholder={
                       chatMode === 'manual' && selectedFiles.length === 0
                         ? "Select files first, then ask your question..."
                         : "Ask about earnings, run scenarios, or request analysis..."
                     }
                     className="chat-input"
                     disabled={isLoading}
                   />
                   <button type="button" className="input-icon">
                     <Mic size={20} />
                   </button>
                   <button type="submit" className="send-btn" disabled={isLoading || !inputValue.trim() || (chatMode === 'manual' && selectedFiles.length === 0)}>
                     <Send size={20} />
                   </button>
                 </div>
               </form>
             </div>
          </div>
        )}
      </div>


      {/* Hierarchical File Selector */}
      <HierarchicalFileSelector
        isOpen={showHierarchicalFileSelector}
        onClose={() => setShowHierarchicalFileSelector(false)}
        onFilesSelected={handleHierarchicalFileSelection}
        selectedFiles={selectedFiles}
        apiBaseUrl={API_BASE_URL}
      />
    </div>
  );
};

export default ChatHome;
